/**
 * PriWelcome — Welcome screen with greeting and context-aware quick-action buttons.
 * Shown when there are no messages yet.
 *
 * S-PRI-2.5: "Hogyan csinaljem?" button shows context-specific label.
 * S-PRI-2.6: "Mi ez az oldal?" button shows current page name.
 */
import { usePriChat } from '@/hooks/usePriChat';
import { usePriStore } from '@/stores/pri-store';
import { useTranslation } from '@kgc/i18n';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getContextualHowToLabel,
  getContextualWhatIsLabel,
  resolveRouteModule,
} from './pri-route-utils';

export function PriWelcome() {
  const { t } = useTranslation();
  const addMessage = usePriStore(s => s.addMessage);
  const markWelcomed = usePriStore(s => s.markWelcomed);
  const { sendQuickAction } = usePriChat();
  const location = useLocation();

  /** Static quick actions that don't depend on page context */
  const staticQuickActions = useMemo(
    () => [
      {
        emoji: '\uD83D\uDCCB',
        label: t('pri.welcome.expiringRentalsLabel'),
        message: t('pri.welcome.expiringRentalsMessage'),
      },
      {
        emoji: '\uD83D\uDD27',
        label: t('pri.welcome.openWorksheetsLabel'),
        message: t('pri.welcome.openWorksheetsMessage'),
      },
    ],
    [t]
  );

  // Resolve current route to module + page name
  const routeInfo = useMemo(() => {
    return resolveRouteModule(location.pathname);
  }, [location.pathname]);

  // Build context-aware labels
  const howToLabel = useMemo(() => {
    if (!routeInfo) return t('pri.welcome.howToFallback');
    return getContextualHowToLabel(routeInfo.pageName);
  }, [routeInfo, t]);

  const whatIsLabel = useMemo(() => {
    if (!routeInfo) return t('pri.welcome.whatIsFallback');
    return getContextualWhatIsLabel(routeInfo.pageName);
  }, [routeInfo, t]);

  function handleQuickAction(message: string) {
    markWelcomed();
    addMessage({ role: 'user', content: message });
  }

  function handleContextAction(type: 'how_to' | 'what_is') {
    markWelcomed();
    const module = routeInfo?.module.toLowerCase() ?? 'unknown';
    const page = routeInfo?.pageName ?? 'unknown';
    void sendQuickAction(type, module, page);
  }

  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-8">
      {/* Avatar */}
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-blue-400 text-3xl shadow-lg">
        🤖
      </div>

      {/* Greeting */}
      <h3 className="mt-4 text-center text-base font-semibold text-white">
        {t('pri.welcome.greeting')}
      </h3>
      <p className="mt-1 text-center text-sm text-slate-400">{t('pri.welcome.subtitle')}</p>

      {/* Quick action grid */}
      <div className="mt-6 grid w-full max-w-[320px] grid-cols-2 gap-2">
        {/* Context-aware: Hogyan csinaljem? */}
        <button
          onClick={() => handleContextAction('how_to')}
          className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-3.5 text-center transition-all hover:border-blue-500/50 hover:bg-slate-700/60"
        >
          <span className="text-xl">{'\u2753'}</span>
          <span className="text-xs font-medium text-slate-300">{howToLabel}</span>
        </button>

        {/* Context-aware: Mi ez az oldal? */}
        <button
          onClick={() => handleContextAction('what_is')}
          className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-3.5 text-center transition-all hover:border-blue-500/50 hover:bg-slate-700/60"
        >
          <span className="text-xl">{'\uD83D\uDCD6'}</span>
          <span className="text-xs font-medium text-slate-300">{whatIsLabel}</span>
        </button>

        {/* Static quick actions */}
        {staticQuickActions.map(action => (
          <button
            key={action.label}
            onClick={() => handleQuickAction(action.message)}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-slate-700 bg-slate-800/50 px-3 py-3.5 text-center transition-all hover:border-blue-500/50 hover:bg-slate-700/60"
          >
            <span className="text-xl">{action.emoji}</span>
            <span className="text-xs font-medium text-slate-300">{action.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
